import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Brain, BookOpen, GitBranch, Code, Terminal, Zap, Layers, Search, Workflow, Settings } from 'lucide-react';
import { useCapabilityPermission } from '@/hooks/useCapabilityPermissions';

// Define ContentType enum properly
type ContentType = 'beginner' | 'intermediate' | 'expert';

const SparcLearningGuide = () => {
  const [activeLevel, setActiveLevel] = useState<ContentType>('beginner');
  const { hasPermission } = useCapabilityPermission('learning-capability-id', 'view');
  
  const learningPathData = {
    beginner: [
      {
        title: "Introduction to SPARC 2.0",
        description: "Learn about the SPARC 2.0 framework and its core capabilities.",
        icon: <Brain className="h-5 w-5 text-primary" />,
        resources: [
          { title: "SPARC 2.0 Overview", type: "article", url: "https://github.com/agenticsorg/sparc2" },
          { title: "Getting Started with SPARC", type: "video", url: "#" }
        ]
      },
      {
        title: "Basic Code Analysis",
        description: "Understand how to analyze code for issues and improvements.",
        icon: <Code className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Code Analysis Fundamentals", type: "article", url: "#" },
          { title: "Your First Code Analysis", type: "tutorial", url: "#" }
        ]
      },
      {
        title: "Execution Environments",
        description: "Learn about secure sandbox environments for code execution.",
        icon: <Terminal className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Sandbox Security", type: "article", url: "#" },
          { title: "Running Code in Sandboxes", type: "tutorial", url: "#" }
        ]
      }
    ],
    intermediate: [
      {
        title: "Vector Similarity Search",
        description: "Explore how to find similar code and patterns across your codebase.",
        icon: <Search className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Understanding Vector Search", type: "article", url: "#" },
          { title: "Advanced Search Techniques", type: "tutorial", url: "#" }
        ]
      },
      {
        title: "Git Integration",
        description: "Master version control integration with SPARC 2.0.",
        icon: <GitBranch className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Checkpoints and Rollbacks", type: "article", url: "#" },
          { title: "Git Workflow Integration", type: "tutorial", url: "#" }
        ]
      },
      {
        title: "Processing Modes",
        description: "Learn about sequential, parallel, and swarm processing modes.",
        icon: <Layers className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Processing Mode Selection", type: "article", url: "#" },
          { title: "Optimizing Performance", type: "tutorial", url: "#" }
        ]
      }
    ],
    expert: [
      {
        title: "MCP Integration",
        description: "Integrate SPARC with Model Context Protocol for AI assistants.",
        icon: <Zap className="h-5 w-5 text-primary" />,
        resources: [
          { title: "MCP Server Setup", type: "article", url: "#" },
          { title: "AI Assistant Integration", type: "tutorial", url: "#" }
        ]
      },
      {
        title: "Custom Workflows",
        description: "Create custom SPARC workflows for your development needs.",
        icon: <Workflow className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Workflow Definition Language", type: "article", url: "#" },
          { title: "Building Complex Workflows", type: "tutorial", url: "#" }
        ]
      },
      {
        title: "Advanced Configuration",
        description: "Deep dive into SPARC configuration options.",
        icon: <Settings className="h-5 w-5 text-primary" />,
        resources: [
          { title: "Configuration File Reference", type: "documentation", url: "#" },
          { title: "Environment Variable Control", type: "tutorial", url: "#" }
        ]
      }
    ]
  };
  
  if (!hasPermission) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="flex items-center">
            <BookOpen className="mr-2 h-5 w-5 text-primary" />
            SPARC Learning Guide
          </CardTitle>
          <CardDescription>
            Access to learning materials requires elevated permissions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p>Contact an administrator to request access to the SPARC learning resources.</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <BookOpen className="mr-2 h-5 w-5 text-primary" />
          SPARC Learning Guide
        </CardTitle>
        <CardDescription>
          Learn how to leverage SPARC 2.0 to enhance your development workflow
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center rounded-md border bg-muted p-1">
              <Button
                variant={activeLevel === 'beginner' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveLevel('beginner')}
              >
                Beginner
              </Button>
              <Button
                variant={activeLevel === 'intermediate' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveLevel('intermediate')}
              >
                Intermediate
              </Button>
              <Button
                variant={activeLevel === 'expert' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveLevel('expert')}
              >
                Expert
              </Button>
            </div>
          </div>
          
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-8">
              {learningPathData[activeLevel].map((module, index) => (
                <div key={index} className="relative">
                  {index < learningPathData[activeLevel].length - 1 && (
                    <div className="absolute left-5 top-12 bottom-0 w-px bg-border" />
                  )}
                  <div className="flex gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border bg-background shadow-sm">
                      {module.icon}
                    </div>
                    <div className="space-y-2">
                      <div className="font-semibold">{module.title}</div>
                      <div className="text-sm text-muted-foreground">{module.description}</div>
                      <div className="pt-2 space-y-2">
                        {module.resources.map((resource, rIndex) => (
                          <a
                            key={rIndex}
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm hover:underline"
                          >
                            <Badge variant="outline">{resource.type}</Badge>
                            {resource.title}
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between border-t pt-4">
        <div className="text-sm text-muted-foreground">
          Learning Path: <span className="font-medium capitalize">{activeLevel}</span>
        </div>
        <Button variant="outline" size="sm">
          Track Progress
        </Button>
      </CardFooter>
    </Card>
  );
};

export default SparcLearningGuide;
